import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { TableLinearLoading } from '@components/Table/TableLinearLoading';
import { TableWrapper } from '@components/Table/TableWrapper';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useFormatter from '@hooks/useFormatter';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { EnumSortDirection } from '@hooks/useRequest';
import { useValidation } from '@hooks/useValidation';
import { useValueState } from '@hooks/useValueState';
import { Box, Card, CardContent, CardHeader, CircularProgress, Typography } from '@material-ui/core';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';
import { INovosInadimplentesFilter, INovosInadimplentesRecord, INovosInadimplentesSummary, useRelatorioService } from '@services/RelatorioService';
import { addCompetence, currentCompetence } from '@utils/Functions';
import { yup } from '@validations/Yup';
import React from 'react';
import { NovosInadimplentesFilter } from './NovosInadimplentesFilter';
import { NovosInadimplentesTable } from './NovosInadimplentesTable';

export type RelatorioNovosInadimplentesProps = {

}

export function RelatorioNovosInadimplentes (props: RelatorioNovosInadimplentesProps) {
    const formatter = useFormatter();
    const summaryData = useValueState<INovosInadimplentesSummary>();

    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();

    const data = useData<INovosInadimplentesRecord, INovosInadimplentesFilter>({
        filter: {
            competencia: useField(addCompetence(currentCompetence(), -1)),
            remetenteWhatsapp: useField()
        },

        sortField: {
            name: "nome",
            direction: EnumSortDirection.Asc
        }
    });
    
    const loading = useLoading();
    const message = useMessage();
    const loadingSummary = useLoading();

    const relatorioService = useRelatorioService();

    const validation = useValidation<INovosInadimplentesFilter>({
        competencia: yup.string().required(),
    })

    const handleCsvButtonClick = React.useCallback(() => {
        if (!loading.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioService.exportarNovosInadimplentes(data.requestedData)
                    .catch(message.showBlobException)
                    .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
        }
    }, [data.filter.setErrorsFromYup, data.requestedData, loading, message.showBlobException, relatorioService, validation]);

    React.useEffect(() => {
        if (!loading.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioService.listarNovosInadimplentes(data.requestedData)
                        .then(data.handleResultResponse)
                        .catch(message.showException)
                        .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    React.useEffect(() => {
        if (!loadingSummary.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loadingSummary.start();
                    relatorioService.sumarizarNovosInadimplentes(data.filter.requestedFilter)
                        .then(summaryData.handleResponse)
                        .catch(message.showException)
                        .finally(loadingSummary.stop);
                })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.filter.requestedFilter]);

    useEffectOnce(() => {
        remetenteWhatsappCombo.loadOptions();
    });

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Relatório de Novos Inadimplentes" />
                </PageTitle>

                <PageContent>
                    <NovosInadimplentesFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                    />

                    <Box style={{marginTop: 24, marginBottom: 24}}>
                        <HorizontalGrid spacing={2}>
                            <GridItem xs={6}>
                                <Card elevation={15}>
                                    <CardHeader title="Total de Novos Inadimplentes" />
                                    <CardContent>
                                        {
                                            loading.isLoading
                                            ? <Box style={{textAlign: 'center'}}><CircularProgress /></Box>
                                            : <Typography variant="h3">{summaryData.value?.count || ""}</Typography>
                                        }
                                    </CardContent>
                                </Card>
                            </GridItem>
                            
                            <GridItem xs={6}>
                                <Card elevation={15}>
                                    <CardHeader title="Valor Total" />
                                    <CardContent>
                                        {
                                            loading.isLoading
                                            ? <Box style={{textAlign: 'center'}}><CircularProgress /></Box>
                                            : <Typography variant="h3">{formatter.toFloat(summaryData.value?.sumValor) || ""}</Typography>
                                        }
                                    </CardContent>
                                </Card>
                            </GridItem>
                        </HorizontalGrid>
                    </Box>

                    <TableWrapper>
                        <TableLinearLoading isLoading={loading.isLoading} />
                        <NovosInadimplentesTable
                            data={data}
                            isLoading={loading.isLoading}
                            onCsvButtonClick={handleCsvButtonClick}
                        />
                    </TableWrapper>
                </PageContent>
            </Main>
        </React.Fragment>
    )
}